/**
 * /payments: get paid as an organizer, and see payments made or received.
 *
 *  - "Get paid": connect a Stripe account (Stripe-hosted onboarding), then
 *    open the Stripe dashboard for balance and payouts.
 *  - "Try a payment" (sandbox only): pick a connected organizer from user
 *    search, confirm who and how much, and test the flow with Stripe's test cards.
 *  - History: what I paid and what I received, with the fee breakdown. The
 *    organizer can refund a paid payment in full from here.
 *
 * Stripe sends the browser back here with ?connect=done|refresh or
 * ?payment=success|cancelled&id=<payment uuid>.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import GameLayout from '../layout/GameLayout';
import paymentService from '../../services/api/paymentService';
import ConfirmModal from '../ui/ConfirmModal';
import UserSearchInput from '../match/UserSearchInput';
import { formatMoney } from '../../utils/money';
import './payments.css';

// Message plus the server's request id, so a screenshot leads straight to the logs
const errorText = (err, fallback) => {
  const data = err?.response?.data || {};
  const message = data.message || fallback;
  return data.requestId ? `${message} (ref ${String(data.requestId).slice(0, 8)})` : message;
};

const STATUS_LABEL = {
  pending: 'Pending',
  succeeded: 'Paid',
  failed: 'Failed',
  expired: 'Not completed',
  refunded: 'Refunded',
};

function ConnectCard({ account, enabled, onError }) {
  const [busy, setBusy] = useState(false);

  const go = async (action) => {
    try {
      setBusy(true);
      onError('');
      const url = action === 'dashboard' ? await paymentService.openDashboard() : await paymentService.startOnboarding();
      window.location.assign(url);
    } catch (err) {
      onError(errorText(err, 'Could not reach Stripe. Please try again.'));
      setBusy(false);
    }
  };

  const ready = account?.chargesEnabled;
  let state = 'Not set up';
  if (ready) {
    state = account.payoutsEnabled ? 'Ready to get paid' : 'Ready to take payments, payouts pending';
  } else if (account?.connected) {
    state = 'Setup not finished';
  }

  return (
    <section className="payments-card" data-testid="payments-connect">
      <h3>Get paid</h3>
      <p className="payments-muted">
        Collect league, tournament and drop-in fees straight to your bank account through Stripe. You
        receive your full price; card fees are added to the player's total.
      </p>
      <p>
        Status: <strong data-testid="payments-connect-state">{state}</strong>
      </p>
      <div className="payments-actions">
        {!ready && (
          <button className="payments-btn" disabled={!enabled || busy} onClick={() => go('onboard')}>
            {account?.connected ? 'Finish Stripe setup' : 'Connect Stripe'}
          </button>
        )}
        {account?.detailsSubmitted && (
          <button className="payments-btn payments-btn--ghost" disabled={busy} onClick={() => go('dashboard')}>
            Open Stripe dashboard
          </button>
        )}
      </div>
    </section>
  );
}

function Breakdown({ q }) {
  if (!q) {
    return null;
  }
  const row = (label, cents, strong) => (
    <div className={`payments-row${strong ? ' payments-row--total' : ''}`}>
      <span>{label}</span>
      <span>{formatMoney(cents, q.currency)}</span>
    </div>
  );
  return (
    <div className="payments-breakdown" data-testid="payments-breakdown">
      {row('Price (organizer receives)', q.priceCents)}
      {q.platformFeeCents > 0 && row('Sportifide fee', q.platformFeeCents)}
      {q.feeTaxCents > 0 && row('Tax on fee', q.feeTaxCents)}
      {q.processingFeeCents > 0 && row('Card processing', q.processingFeeCents)}
      {row('Player pays', q.totalCents, true)}
    </div>
  );
}

const fullName = (u) => [u?.first_name, u?.last_name].filter(Boolean).join(' ');

function TestCheckoutCard({ onError, userId }) {
  const [organizer, setOrganizer] = useState(null);
  const [amount, setAmount] = useState('12.00');
  const [q, setQ] = useState(null);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  // Stable, so the search doesn't re-run on every render
  const excludeIds = useMemo(() => (userId ? [userId] : []), [userId]);

  const priceCents = Math.round(Number(amount) * 100);

  useEffect(() => {
    if (!Number.isFinite(priceCents) || priceCents < 100) {
      setQ(null);
      return undefined;
    }
    const t = setTimeout(() => {
      paymentService.getQuote(priceCents).then(setQ).catch(() => setQ(null));
    }, 300);
    return () => clearTimeout(t);
  }, [priceCents]);

  const pay = async () => {
    try {
      setBusy(true);
      onError('');
      const { url } = await paymentService.startTestCheckout({ recipientUsername: organizer.username, priceCents });
      window.location.assign(url);
    } catch (err) {
      setAsking(false);
      onError(errorText(err, 'Could not start the payment.'));
      setBusy(false);
    }
  };

  const total = q ? formatMoney(q.totalCents, q.currency) : '';
  const who = organizer ? `${fullName(organizer) || organizer.username} (@${organizer.username})` : '';

  return (
    <section className="payments-card" data-testid="payments-test">
      <h3>Try a payment (sandbox)</h3>
      <p className="payments-muted">
        Test mode: no real money moves. Pay with card 4242 4242 4242 4242, any future date and any CVC.
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setAsking(true);
        }}
        className="payments-form"
      >
        {/* Pick a real user from search, so money can't go to a mistyped name */}
        <UserSearchInput
          label="Organizer"
          placeholder="Search organizer by username or name"
          selectedUser={organizer}
          onSelect={setOrganizer}
          onClear={() => setOrganizer(null)}
          excludeUserIds={excludeIds}
          disabled={busy}
        />
        <label>
          Price (CAD)
          <input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required />
        </label>
        <Breakdown q={q} />
        <button className="payments-btn" type="submit" disabled={busy || !organizer || !q}>
          {q ? `Pay ${total}` : 'Pay'}
        </button>
      </form>
      <ConfirmModal
        isOpen={asking}
        title="Confirm payment"
        message={`Pay ${total} to ${who}? Check this is the right organizer.`}
        confirmText={`Pay ${total}`}
        isLoading={busy}
        onConfirm={pay}
        onCancel={() => setAsking(false)}
      />
    </section>
  );
}

function RefundButton({ payment, onDone, onError }) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const amount = formatMoney(payment.total_cents, payment.currency);

  const refund = async () => {
    try {
      setBusy(true);
      onError('');
      await paymentService.refundPayment(payment.uuid);
      setAsking(false);
      await onDone();
    } catch (err) {
      setAsking(false);
      onError(errorText(err, 'Could not refund. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button className="payments-btn payments-btn--ghost payments-btn--small" disabled={busy} onClick={() => setAsking(true)}>
        {busy ? 'Refunding…' : 'Refund'}
      </button>
      <ConfirmModal
        isOpen={asking}
        title="Refund this payment?"
        message={`@${payment.payer_username || 'player'} gets ${amount} back. It comes out of your Stripe balance and can't be undone.`}
        confirmText={`Refund ${amount}`}
        isDanger
        isLoading={busy}
        onConfirm={refund}
        onCancel={() => setAsking(false)}
      />
    </>
  );
}

function History({ payments, userId, onChanged, onError }) {
  if (!payments.length) {
    return <p className="payments-muted">No payments yet.</p>;
  }
  return (
    <ul className="payments-history" data-testid="payments-history">
      {payments.map((p) => {
        const received = p.recipient_user_id === userId;
        return (
          <li key={p.uuid}>
            <div>
              <strong>{p.description || 'Payment'}</strong>
              <div className="payments-muted">
                {received ? `From @${p.payer_username || 'player'}` : `To @${p.recipient_username || 'organizer'}`}
                {' · '}
                {new Date(p.created_at).toLocaleDateString()}
              </div>
            </div>
            <div className="payments-history__amount">
              <span>{formatMoney(received ? p.price_cents : p.total_cents, p.currency)}</span>
              <span className={`payments-status payments-status--${p.status}`}>{STATUS_LABEL[p.status] || p.status}</span>
              {received && p.status === 'succeeded' && <RefundButton payment={p} onDone={onChanged} onError={onError} />}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function PaymentsPage({ token, onLogout }) {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [account, setAccount] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const cancelledId = params.get('payment') === 'cancelled' ? params.get('id') : null;
      if (cancelledId) {
        // Close the Stripe session so History shows "Not completed", not Pending
        await paymentService.cancelPayment(cancelledId).catch(() => {});
      }
      const cfg = await paymentService.getConfig();
      setConfig(cfg);
      const [acct, list] = await Promise.all([
        paymentService.getConnectStatus(),
        paymentService.listPayments(),
      ]);
      setAccount(acct);
      setPayments(list || []);
    } catch (err) {
      setError(errorText(err, 'Could not load payments.'));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const refreshPayments = useCallback(async () => {
    setPayments((await paymentService.listPayments()) || []);
  }, []);

  const notice = useMemo(() => {
    if (params.get('payment') === 'success') {
      return 'Payment received by Stripe. It shows as Paid here once Stripe confirms it (a few seconds).';
    }
    if (params.get('payment') === 'cancelled') {
      return 'Payment cancelled. You were not charged.';
    }
    if (params.get('connect') === 'done') {
      return 'Stripe setup saved.';
    }
    if (params.get('connect') === 'refresh') {
      return 'The Stripe link expired. Press the button again to continue.';
    }
    return '';
  }, [params]);

  return (
    <GameLayout token={token} onLogout={onLogout} profile={user}>
      <div className="payments-page">
        <h2>Payments</h2>
        {config?.enabled && !config.livemode && (
          <div className="payments-banner" data-testid="payments-sandbox-banner">Sandbox: test payments only</div>
        )}
        {notice && <div className="payments-notice" role="status">{notice}</div>}
        {error && <div className="payments-error" role="alert">{error}</div>}
        {loading ? (
          <p className="payments-muted">Loading…</p>
        ) : (
          <>
            {!config?.enabled && (
              <div className="payments-notice" data-testid="payments-off">
                In-app payments are coming soon. Hosting and playing stay free during beta.
              </div>
            )}
            {config?.enabled && <ConnectCard account={account} enabled onError={setError} />}
            {config?.enabled && !config.livemode && <TestCheckoutCard onError={setError} userId={user?.id} />}
            <section className="payments-card">
              <h3>History</h3>
              <History payments={payments} userId={user?.id} onChanged={refreshPayments} onError={setError} />
            </section>
          </>
        )}
      </div>
    </GameLayout>
  );
}

export default PaymentsPage;
